import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnInit,
  SimpleChanges,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';

export type AvailabilityLevel = 'high' | 'medium' | 'low' | 'none' | 'unknown';

export interface DayAvailability {
  date: string; // YYYY-MM-DD
  totalSlots: number;
  availableSlots: number;
  level: AvailabilityLevel;
}

@Component({
  selector: 'app-availability-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './availability-calendar.html',
  styleUrl: './availability-calendar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityCalendarComponent implements OnInit, OnChanges {
  @Input() businessSchedule: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive: boolean;
  }> = [];
  @Input() dayAvailabilities: DayAvailability[] = [];
  @Input() selectedDate: string | null = null;
  @Input() primaryColor: string = '#2563eb';
  @Input() isLoading: boolean = false;

  @Output() dateSelected = new EventEmitter<string>();
  @Output() monthChanged = new EventEmitter<{ year: number; month: number }>();

  private currentMonth = signal(new Date());
  weeks = signal<Date[][]>([]);
  private availabilityMap = signal<Map<string, DayAvailability>>(new Map());

  weekDays = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

  currentMonthLabel = computed(() => {
    const d = this.currentMonth();
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    ];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  });

  canGoPrevious = computed(() => {
    const now = new Date();
    const current = this.currentMonth();
    return (
      current.getFullYear() > now.getFullYear() ||
      (current.getFullYear() === now.getFullYear() && current.getMonth() > now.getMonth())
    );
  });

  ngOnInit(): void {
    // Start on the month of the selected date or today
    if (this.selectedDate) {
      const parts = this.selectedDate.split('-').map(Number);
      this.currentMonth.set(new Date(parts[0], parts[1] - 1, 1));
    } else {
      this.currentMonth.set(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    }
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dayAvailabilities']) {
      const map = new Map<string, DayAvailability>();
      (this.dayAvailabilities || []).forEach(da => map.set(da.date, da));
      this.availabilityMap.set(map);
    }
  }

  previousMonth(): void {
    if (!this.canGoPrevious()) return;
    const current = this.currentMonth();
    const newMonth = new Date(current.getFullYear(), current.getMonth() - 1, 1);
    this.currentMonth.set(newMonth);
    this.generateCalendar();
    this.monthChanged.emit({ year: newMonth.getFullYear(), month: newMonth.getMonth() + 1 });
  }

  nextMonth(): void {
    const current = this.currentMonth();
    const newMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    this.currentMonth.set(newMonth);
    this.generateCalendar();
    this.monthChanged.emit({ year: newMonth.getFullYear(), month: newMonth.getMonth() + 1 });
  }

  selectDay(date: Date): void {
    if (this.isDayDisabled(date)) return;
    const formatted = this.formatDate(date);
    this.dateSelected.emit(formatted);
  }

  isDayInCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.currentMonth().getMonth();
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  isSelected(date: Date): boolean {
    if (!this.selectedDate) return false;
    return this.formatDate(date) === this.selectedDate;
  }

  isDayDisabled(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    if (!this.isDayInCurrentMonth(date)) return true;

    // Check if day has schedule
    const dayOfWeek = date.getDay();
    const hasSchedule = this.businessSchedule.some(
      r => r.dayOfWeek === dayOfWeek && r.isActive
    );
    if (!hasSchedule) return true;

    // Fully booked or blocked: no slot fits the selected service
    const availability = this.getDayAvailability(date);
    if (availability && availability.availableSlots === 0) return true;

    return false;
  }

  getDayAvailability(date: Date): DayAvailability | null {
    const key = this.formatDate(date);
    return this.availabilityMap().get(key) ?? null;
  }

  getAvailabilityLevel(date: Date): AvailabilityLevel {
    if (!this.isDayInCurrentMonth(date)) return 'unknown';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return 'unknown';

    const dayOfWeek = date.getDay();
    const hasSchedule = this.businessSchedule.some(
      r => r.dayOfWeek === dayOfWeek && r.isActive
    );
    if (!hasSchedule) return 'none';

    const availability = this.getDayAvailability(date);
    if (!availability) return 'unknown';
    return availability.level;
  }

  getAvailabilityText(date: Date): string {
    if (this.isDayDisabled(date)) return 'sin disponibilidad';
    switch (this.getAvailabilityLevel(date)) {
      case 'high':
        return 'disponible';
      case 'medium':
        return 'pocos horarios';
      case 'low':
        return 'últimos horarios';
      default:
        return 'disponible';
    }
  }

  private generateCalendar(): void {
    const month = this.currentMonth();
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

    // Start from Sunday of the first week
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End at Saturday of the last week
    const endDate = new Date(lastDay);
    const remaining = 6 - endDate.getDay();
    endDate.setDate(endDate.getDate() + remaining);

    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    const cursor = new Date(startDate);

    while (cursor <= endDate) {
      currentWeek.push(new Date(cursor));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    this.weeks.set(weeks);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
